import { JobDetails } from '@/reducers/jbpGeneralSlice';
import { FormMultiButtonGroup } from '@portal_components/components/FormMultiButtonGroup';
import * as Yup from 'yup';
import { FormTextArea } from '@portal_components/components/FormTextArea';
import { IndicatorType } from '@portal_components/components/WorkflowIndicator';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { FormTagGroup } from '@portal_components/components/FormTagGroup';
import { useMemo } from 'react';
import { useAppSelector } from '@/store';
import { useUserInfo } from '@/hooks/useUserInfo';
import { FormWorkflowView } from '@portal_components/formWorkflow/FormWorkflowView';
import { getJobDatesNode, jobCommentsNode, siteAddressNode, siteContactDetailsNode, terminateJobBookingNode } from '../GenericNodes';
import { Title } from '@portal_components/components/Title';

interface FormBookJobSiteInfoProps {
  mpan: string;
  jobTypeCode: string;
  handleSubmit: (values: JobDetails) => void;
}

export const FormBookJobSiteInfo = ({ mpan, jobTypeCode, handleSubmit }: FormBookJobSiteInfoProps) => {
  const { site } = useAppSelector(state => state.jbp);
  const { canAccess } = useUserInfo();
  const canAccessExtraSkills = canAccess('canSelectExtraSkills');

  const shouldShowPowerRelatedQuestions = useMemo(() => {
    const powerRelatedJobTypes = ['METER_DOWNGRADE', 'METER_UPGRADE', 'COMC'];
    const requiresWholeCurrent = ['CHECK_METER_INSTALL', 'METER_REPLACEMENT'];

    return powerRelatedJobTypes.includes(jobTypeCode) || (requiresWholeCurrent.includes(jobTypeCode) && site?.isWCJob);
  }, [jobTypeCode, site]);

  if (!site) return null;
  return (
    <div className="ml-10 pb-4 mb-10">
      <Title className="mt-7 mb-5" text={`Booking for: ${mpan}`} />
      <FormWorkflowView<JobDetails>
        onSubmit={handleSubmit}
        initialValues={{
          siteContactDetails: site.siteContactDetails,
          siteAddress: site.siteAddress
        }}
        nodes={[
          {
            element: shouldShowPowerRelatedQuestions ? (
              <FormMultiButtonGroup
                name="customerAgreesToPowerDown"
                title="Customer agrees to power being turned off?"
                description="The power will need to be turned off for the duration of the job, which will impact the use of electrical equipment on site"
                options={[
                  { title: 'No', value: false },
                  { title: 'Yes', value: true }
                ]}
              />
            ) : null,
            validationSchema: shouldShowPowerRelatedQuestions ? { customerAgreesToPowerDown: Yup.boolean().required().isTrue() } : undefined,
            breakRenderingOnInvalid: true,
            children: (values: JobDetails) => {
              if (shouldShowPowerRelatedQuestions && values.customerAgreesToPowerDown === false) {
                return terminateJobBookingNode;
              }
            }
          },
          {
            element: shouldShowPowerRelatedQuestions ? (
              <FormMultiButtonGroup
                name="liftOnSite"
                title="Is there a lift on site?"
                options={[
                  { title: 'No', value: false },
                  { title: 'Yes', value: true }
                ]}
              />
            ) : null,
            validationSchema: shouldShowPowerRelatedQuestions ? { liftOnSite: Yup.boolean().required() } : undefined,
            children: (values: JobDetails) => {
              if (shouldShowPowerRelatedQuestions && values.liftOnSite) {
                return {
                  element: <Title className="mt-7 mb-5 font-italic text-sm" text="Please be advised that the lift will need to be identifiable as out of use for the duration of the job" />,
                  classname: 'bg-elevation-1 mr-8',
                  indicatorType: IndicatorType.Info
                };
              }
            }
          },
          {
            element: (
              <FormMultiButtonGroup
                name="siteInductionRequired"
                title="Is a site induction required (for the field operative)?"
                options={[
                  { title: 'No', value: false },
                  { title: 'Yes', value: true }
                ]}
              />
            ),
            validationSchema: { siteInductionRequired: Yup.boolean().required().isFalse() },
            breakRenderingOnInvalid: true,
            children: (values: JobDetails) => {
              if (values.siteInductionRequired) {
                return terminateJobBookingNode;
              }
            }
          },
          {
            element: (
              <FormMultiButtonGroup
                name="parkingAvailable"
                title="Is Parking Available?"
                options={[
                  { title: 'No', value: false },
                  { title: 'Yes', value: true }
                ]}
              />
            ),
            validationSchema: { parkingAvailable: Yup.boolean().required().isTrue() },
            breakRenderingOnInvalid: true,
            children: (values: JobDetails) => {
              if (values.parkingAvailable === false) {
                return terminateJobBookingNode;
              }
            }
          },
          {
            name: 'accessInstructions',
            element: (
              <div>
                <Title text="Please provide any additional information regarding parking, access, or directions" className="mb-5 mt-7" />
                <FormTextArea name="accessInstructions" title="Access Instructions" placeholder="Tell us how to access the site including detailed descriptions." className="w-[30em]" />
              </div>
            ),
            indicatorType: IndicatorType.Default,
            validationSchema: { accessInstructions: Yup.string().optional() }
          },
          {
            element: (
              <FormTagGroup
                title="Please provide additional information regarding parking & access"
                name="parkingInfo"
                classname="mr-10"
                options={[
                  {
                    tag: 'Free Parking',
                    turnOff: ['Pay & Display', 'Payment App (via phone)']
                  },
                  {
                    tag: 'Onsite Car Park'
                  },
                  {
                    tag: 'Pay & Display',
                    turnOff: ['Free Parking']
                  },
                  {
                    tag: 'Permit Parking (Customer to provide)'
                  },
                  {
                    tag: 'Payment App (via phone)',
                    turnOff: ['Free Parking']
                  },
                  {
                    tag: 'Short Walk',
                    turnOff: ['Long Walk']
                  },
                  {
                    tag: 'Long Walk',
                    turnOff: ['Short Walk']
                  },
                  {
                    tag: 'Time of Day Parking Restrictions'
                  },
                  {
                    tag: 'Vehicle height restrictions'
                  }
                ].map(option => ({ title: option.tag, value: option.tag, turnsOff: option.turnOff }))}
              />
            ),
            indicatorType: IndicatorType.Default,
            validationSchema: { parkingInfo: Yup.array().of(Yup.string()) }
          },
          {
            name: 'whatThreeWords',
            element: (
              <div>
                <Title text="If known, please provide the What 3 Words location information" className="mb-5 mt-7" />
                <FormTextArea
                  name="whatThreeWords"
                  title="What 3 Words Location Information"
                  placeholder="What 3 Words reference in the following format: wordone.wordtwo.wordthree"
                  className="w-[30em]"
                />
              </div>
            ),
            indicatorType: IndicatorType.Default,
            validationSchema: { whatThreeWords: Yup.string().optional() }
          },
          {
            element: (
              <FormMultiButtonGroup
                name="meterAccessible"
                title="Is the meter accessible?"
                description="The metering equipment will need to be easily accessible, free from obstructions, and positioned no higher than 1.8m from the ground."
                options={[
                  { title: 'No', value: false },
                  { title: 'Yes', value: true }
                ]}
              />
            ),
            validationSchema: { meterAccessible: Yup.boolean().required().isTrue() },
            breakRenderingOnInvalid: true,
            children: (values: JobDetails) => {
              if (values.meterAccessible === false) {
                return terminateJobBookingNode;
              }
            }
          },
          {
            element: canAccessExtraSkills ? (
              <FormTagGroup
                title="Please select any required extra skills"
                name="extraSkills"
                classname="mr-10"
                options={[
                  {
                    title: '4x4',
                    value: '4x4'
                  }
                ]}
              />
            ) : null
          },
          {
            element: (
              <FormMultiButtonGroup
                name="ramsRequired"
                title="Are risk assessments or method statements required (RAMS)?"
                options={[
                  { title: 'No', value: false },
                  { title: 'Yes', value: true }
                ]}
              />
            ),
            validationSchema: {
              ramsRequired: Yup.boolean().required(),
              ramsEmail: Yup.string().when('ramsRequired', {
                is: true,
                then: schema => schema.required('Please provide an email address for RAMS notifications').email('Please enter a valid email address'),
                otherwise: schema => schema.optional()
              })
            },
            children: (values: JobDetails) => {
              if (values.ramsRequired) {
                return {
                  name: 'ramsEmail',
                  element: (
                    <div>
                      <Title text="Please confirm the email address to be used for RAMS notifications" className="mb-5 mt-7" />
                      <FormTextInput name="ramsEmail" title="Email for RAMS notifications" placeholder="Please enter a valid email" className="w-[30em]" />
                    </div>
                  )
                };
              }
            }
          },
          siteContactDetailsNode,
          {
            element: (
              <Title
                className="mt-7 mb-5 font-italic text-sm"
                text="Please be advised updates to address details below will not update the details held in our portfolio. To do so please submit a D0302 flow file"
              />
            ),
            classname: 'bg-elevation-1 mr-8',
            indicatorType: IndicatorType.Info
          },
          siteAddressNode,
          jobCommentsNode,
          getJobDatesNode
        ]}
      />
    </div>
  );
};
